/**
 * Practice Arena - ChatGPT-style two-panel orchestrator.
 *
 * Left panel  : PracticeHistorySidebar (via MindspaceLayout's panel slot)
 * Right panel : depends on `phase`
 *   - setup    → PracticeSetup (smart-start cards + custom session)
 *   - session  → FlashcardRunner | QuizSessionShell
 *   - results  → SessionResults (just-finished run)
 *   - viewing  → SessionDetailView (a past session selected from sidebar)
 *
 * URL state:
 *   ?mode=flashcard|quiz   - sticky mode preference
 *   ?session=<id>          - currently-open detail view (re-hydrates on refresh)
 *
 * Mid-session safety: if the user starts a new session (or clicks a
 * past-session row in the sidebar) while one is running, or the runner
 * crashes, the rated outcomes are recorded to history as an `incomplete`
 * session (`flushPartialIfRunning`) before transitioning. A run with nothing
 * rated writes nothing. ChatGPT-style "just switch" UX.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { History, Loader2 } from 'lucide-react';
import { ErrorBoundary } from '@/components/core/common/boundaries/ErrorBoundary';
import { MindspaceLayout } from '@/components/features/mindspace/MindspaceLayout';
import { FlashcardRunner } from '@/components/features/mindspace/practice/runners/FlashcardRunner';
import { QuizSessionShell } from '@/components/features/mindspace/practice/runners/QuizSessionShell';
import { RunnerFocusShell } from '@/components/features/mindspace/practice/runners/RunnerFocusShell';
import { PracticeSetup } from '@/components/features/mindspace/practice/session/PracticeSetup';
import { PracticeHistorySidebar } from '@/components/features/mindspace/practice/session/PracticeHistorySidebar';
import { SessionResults } from '@/components/features/mindspace/practice/session/SessionResults';
import { SessionDetailView } from '@/components/features/mindspace/practice/session/SessionDetailView';
import { RunnerErrorFallback } from '@/components/features/mindspace/practice/session/RunnerErrorFallback';
import { StreakBadge } from '@/components/features/mindspace/practice/widgets/StreakBadge';
import { scopeLabel } from '@/components/features/mindspace/practice/utils';
import { track } from '@/config/observability';
import { buildSessionRecord } from '@/lib/practice/sessionRecord';
import { waitForReviewsToSettle } from '@/lib/practice/waitForReviewsToSettle';
import type {
  PracticeMode,
  SessionConfig,
  SessionOutcome,
} from '@/lib/practice/session-types';
import { revisionKeys } from '@/hooks/mindspace/useRevision';
import {
  useRecordPracticeSession,
  usePracticeHistoryState,
} from '@/hooks/mindspace/usePracticeHistory';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { useHideNavbarOnPhone } from '@/contexts/PhoneChromeContext';
import { PracticePhoneHeader } from '@/components/features/mindspace/practice/session/PracticePhoneHeader';

type Phase = 'setup' | 'session' | 'results' | 'viewing';

// usePracticeSession owns the quiz default-length now.
const DEFAULT_REPLAY_LENGTH = 20;

export function PracticeArenaPage() {
  // Activity tracking - time spent in Practice Arena counts as study time
  // and feeds the StudySession activity_count toward the streak.
  useActivityTracking({ contentType: 'practice_arena' });

  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---- URL-derived state ----
  const mode: PracticeMode =
    searchParams.get('mode') === 'quiz' ? 'quiz' : 'flashcard';
  const viewingSessionId = searchParams.get('session');

  // ---- Session state ----
  const [config, setConfig] = useState<SessionConfig | null>(null);
  // Client-generated UUID per session start. Serves two purposes:
  //   1. React key on the runners - every new session forces a fresh
  //      mount so stale runner state (index, outcomes, lastRating)
  //      cannot bleed across sessions even if the same mode is reused.
  //   2. Idempotency key on `recordSession.mutate` - the backend
  //      upserts on `(user_id, client_session_id)` so a network retry
  //      or rapid double-flush can't create duplicate history rows.
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<SessionOutcome[]>([]);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [showResults, setShowResults] = useState(false);
  // The runner threw and its boundary is showing the fallback. The session
  // is over at that point — its partial is flushed — so the sidebar must stop
  // listing it as "Practicing now" even though `config` is still set.
  const [runnerCrashed, setRunnerCrashed] = useState(false);
  const sessionStart = useRef<number>(0);

  // Mirror of the runner's in-progress outcomes so we can save a
  // partial record when the user switches sessions mid-run. Kept in a
  // ref to avoid re-rendering the orchestrator on every rating.
  const liveOutcomesRef = useRef<SessionOutcome[]>([]);
  // The runner's frozen deck size, reported with every progress snapshot, so
  // a partial record says "1 of 3" rather than "1 of 1". Stays 0 until the
  // first rating — and with nothing rated, nothing is written anyway.
  const liveDeckSizeRef = useRef(0);

  // Set of session UUIDs we've already flushed/recorded. Gates
  // `flushPartialIfRunning` against double-fire when the user
  // rapid-clicks sidebar rows - the second call sees the id is
  // already in the set and bails out before mutating again.
  const recordedSessionsRef = useRef<Set<string>>(new Set());

  // Sessions we've recorded a *completion* for. Separate from
  // `recordedSessionsRef` so a finish can still promote a prior partial
  // flush (incomplete → complete via the backend upsert) exactly once,
  // without a double-fire if `finishSession` is ever called twice.
  const completedSessionsRef = useRef<Set<string>>(new Set());

  // ---- Derived phase ----
  // Priority order: session in progress wins; results screen second;
  // viewing past session third; otherwise setup.
  const phase: Phase =
    config && !showResults
      ? 'session'
      : showResults
        ? 'results'
        : viewingSessionId
          ? 'viewing'
          : 'setup';

  // ---- Phone focus mode ----
  // Below `md` a running session takes the whole screen: the runner draws its
  // own bar (End, progress), so the page header, the Back row and the app
  // navbar step away until it ends. The History sheet is opened from the
  // phone header's labelled button.
  const compact = useMediaQuery('(max-width: 767.98px)');
  const inSession = compact && phase === 'session';
  useHideNavbarOnPhone(phase === 'session');
  const [historyOpen, setHistoryOpen] = useState(false);

  // ---- History ----
  const {
    sessions: history,
    isLoading: historyLoading,
    loadFailed: historyFailed,
  } = usePracticeHistoryState();
  const recordSession = useRecordPracticeSession();
  const viewingRecord = useMemo(
    () => history.find(r => r.id === viewingSessionId) ?? null,
    [history, viewingSessionId]
  );

  // If the URL points to a session that no longer exists (cleared
  // history, manual URL edit), drop the param so we fall back to setup.
  //
  // Gated on the history having RESOLVED. Without the gate this fires on the
  // first render of every cold load — `usePracticeHistory` answers `[]` while
  // its fetch is still in flight, so `viewingRecord` is null, the param is
  // stripped, and `?session=<id>` never re-opens anything on a refresh. The
  // self-heal was quietly eating the rehydrate it shares a URL param with.
  // A FAILED read is not "resolved" either: it says nothing about whether the
  // session exists, so it must not strip the param a Retry could still open.
  useEffect(() => {
    if (historyLoading || historyFailed) return;
    if (viewingSessionId && !viewingRecord) {
      const params = new URLSearchParams(searchParams);
      params.delete('session');
      setSearchParams(params, { replace: true });
    }
  }, [
    historyLoading,
    historyFailed,
    viewingSessionId,
    viewingRecord,
    searchParams,
    setSearchParams,
  ]);

  const setMode = (next: PracticeMode) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'flashcard') params.delete('mode');
    else params.set('mode', next);
    setSearchParams(params, { replace: true });
  };

  const clearViewing = () => {
    if (!viewingSessionId) return;
    const params = new URLSearchParams(searchParams);
    params.delete('session');
    setSearchParams(params, { replace: true });
  };

  const openSessionDetail = (id: string) => {
    // If a session is running, flush a partial record first so the
    // user doesn't lose visibility of what they were doing.
    flushPartialIfRunning();
    const params = new URLSearchParams(searchParams);
    params.set('session', id);
    setSearchParams(params, { replace: true });
    setConfig(null);
    setSessionId(null);
    setOutcomes([]);
    setShowResults(false);
    setRunnerCrashed(false);
    liveOutcomesRef.current = [];
    liveDeckSizeRef.current = 0;
  };

  const flushPartialIfRunning = () => {
    if (!config || !sessionId) return;
    // Already recorded this session - second flush from a rapid
    // sidebar-click sequence would otherwise insert a duplicate row.
    if (recordedSessionsRef.current.has(sessionId)) return;
    const liveOutcomes = liveOutcomesRef.current;
    if (liveOutcomes.length === 0) return; // nothing meaningful to save
    recordedSessionsRef.current.add(sessionId);
    recordSession.mutate(
      buildSessionRecord({
        clientSessionId: sessionId,
        config,
        outcomes: liveOutcomes,
        deckSize: liveDeckSizeRef.current,
        durationMs: Date.now() - sessionStart.current,
      })
    );
  };

  const trackProgress = (next: SessionOutcome[], deckSize: number) => {
    liveOutcomesRef.current = next;
    liveDeckSizeRef.current = deckSize;
  };

  const startSession = (cfg: SessionConfig) => {
    flushPartialIfRunning();
    clearViewing();
    setConfig(cfg);
    setSessionId(crypto.randomUUID());
    setOutcomes([]);
    setSessionTotal(0);
    setElapsedMs(0);
    setShowResults(false);
    setRunnerCrashed(false);
    liveOutcomesRef.current = [];
    liveDeckSizeRef.current = 0;
    sessionStart.current = Date.now();
    track('practice_session_started', {
      mode: cfg.mode,
      scope: cfg.scope,
      length: cfg.length,
    });
  };

  const finishSession = async (final: SessionOutcome[], total?: number) => {
    // Nothing rated: there is no result to show and nothing to record — the
    // same rule the partial and crash flushes follow. Back to setup instead
    // of an empty "Ended early · Practiced 0 of N" screen and a noise row.
    if (final.length === 0) {
      goSetup();
      return;
    }
    const resolvedTotal = total ?? final.length;
    const duration = Date.now() - sessionStart.current;
    setOutcomes(final);
    setSessionTotal(resolvedTotal);
    setElapsedMs(duration);
    setShowResults(true);

    if (config && sessionId && !completedSessionsRef.current.has(sessionId)) {
      // Record exactly once. A prior partial flush (if any) is promoted by
      // the backend upsert on client_session_id; guarding on a dedicated
      // completed-set means a double `finishSession` call can't fire twice.
      // Ending the session before clearing the whole deck (the floating
      // "End" button) is still an INCOMPLETE run - only finishing every
      // concept counts as complete.
      completedSessionsRef.current.add(sessionId);
      recordedSessionsRef.current.add(sessionId);
      track('practice_session_finished', {
        mode: config.mode,
        scope: config.scope,
        answered: final.length,
        duration_s: Math.round(duration / 1000),
      });
      recordSession.mutate(
        buildSessionRecord({
          clientSessionId: sessionId,
          config,
          outcomes: final,
          deckSize: resolvedTotal,
          durationMs: duration,
        })
      );
    }
    liveOutcomesRef.current = [];
    liveDeckSizeRef.current = 0;

    // The runners fire FSRS reviews fire-and-forget; wait for those writes to
    // commit before refetching the daily plan so the "Due today" count
    // reflects the reviews just made (otherwise the recompute races the
    // in-flight writes and the completed concepts still show as due).
    await waitForReviewsToSettle(qc, {
      mutationKey: revisionKeys.submitReview(),
    });
    qc.invalidateQueries({ queryKey: revisionKeys.all });
  };

  const goSetup = () => {
    flushPartialIfRunning();
    clearViewing();
    setConfig(null);
    setSessionId(null);
    setOutcomes([]);
    setShowResults(false);
    setRunnerCrashed(false);
    liveOutcomesRef.current = [];
    liveDeckSizeRef.current = 0;
  };

  // The runner's boundary caught. Save what was rated through the same
  // partial-flush path a mid-session switch uses — which writes nothing when
  // nothing was rated — and end the session. `liveOutcomesRef` is left as is:
  // the fallback reads it to say which of those two happened.
  const handleRunnerCrash = () => {
    flushPartialIfRunning();
    setRunnerCrashed(true);
  };

  /**
   * Sidebar back-button handler - phase-aware:
   *   - setup phase: navigate to /mindspace (real back).
   *   - any other phase: rewind to the setup screen instead of leaving
   *     Practice Arena. Flushes any in-progress session as `incomplete`
   *     so partial work survives in the history list.
   */
  const handleBack = () => {
    if (phase === 'setup') {
      navigate('/mindspace');
      return;
    }
    goSetup();
  };

  return (
    <MindspaceLayout
      panelOpen={compact ? historyOpen : undefined}
      onPanelOpenChange={setHistoryOpen}
      // A phone session is a focus mode: no Back row, no header, no navbar.
      // End (✕ in the runner's bar) is the way out, as it always was.
      bare={inSession}
      panelLabel='History'
      panelIcon={<History className='h-5 w-5' />}
      onBack={handleBack}
      panelSelection={`${phase}:${viewingSessionId ?? ''}`}
      filterPanel={
        <PracticeHistorySidebar
          phase={phase}
          selectedSessionId={viewingSessionId}
          activeSession={
            phase === 'session' && config && !runnerCrashed
              ? { scope: config.scope, mode: config.mode }
              : null
          }
          onSelectSession={openSessionDetail}
          onNewSession={goSetup}
        />
      }
    >
      <div className='flex h-full flex-col'>
        {compact ? (
          !inSession && (
            <PracticePhoneHeader
              historyOpen={historyOpen}
              onOpenHistory={() => setHistoryOpen(true)}
            />
          )
        ) : (
          <header className='flex h-16 flex-shrink-0 items-center gap-2 border-b border-border px-4'>
            <h2 className='text-xl font-semibold text-foreground'>
              Practice Arena
            </h2>
            <StreakBadge />
            {phase === 'session' && config && (
              <span className='dark:tint-brand ml-2 rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700'>
                {config.mode === 'flashcard' ? 'Flashcards' : 'Quiz'} ·{' '}
                {scopeLabel(config.scope)}
              </span>
            )}
          </header>
        )}

        {/* Body - strict flex column without its own overflow so the
            runners can rely on h-full filling the available space.
            Other phases scroll inside their own wrappers. */}
        <div className='flex min-h-0 flex-1 flex-col'>
          {phase === 'setup' && (
            <div className='flex min-h-0 flex-1 flex-col'>
              <PracticeSetup
                mode={mode}
                onModeChange={setMode}
                onStart={startSession}
              />
            </div>
          )}

          {phase === 'session' && config && sessionId && (
            // Wrap in ErrorBoundary so a throw inside a runner callback
            // (rare but possible - keyboard handler, network races)
            // doesn't take down the sidebar too. On a crash the partial is
            // flushed and the fallback offers a path back to setup rather
            // than reload-the-page.
            // `key={sessionId}` forces a fresh mount per session start -
            // belt-and-braces against the (now-impossible) case where
            // the same runner instance handles two sessions in a row
            // and inherits the prior session's `outcomes` / `index`.
            <ErrorBoundary
              key={sessionId}
              onError={handleRunnerCrash}
              fallback={
                <RunnerErrorFallback
                  outcomesRef={liveOutcomesRef}
                  onBackToSetup={goSetup}
                />
              }
            >
              <RunnerFocusShell sessionId={sessionId}>
                {config.mode === 'flashcard' ? (
                  <FlashcardRunner
                    config={config}
                    onEnd={finishSession}
                    onAbort={goSetup}
                    onProgress={trackProgress}
                  />
                ) : (
                  <QuizSessionShell
                    config={config}
                    onEnd={finishSession}
                    onAbort={goSetup}
                    onProgress={trackProgress}
                  />
                )}
              </RunnerFocusShell>
            </ErrorBoundary>
          )}

          {phase === 'results' && (
            <div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
              <SessionResults
                outcomes={outcomes}
                total={sessionTotal}
                // Same verdict `finishSession` records to history.
                incomplete={outcomes.length < sessionTotal}
                elapsedMs={elapsedMs}
                scope={config?.scope ?? 'due'}
                onPracticeWeak={() => {
                  const weakIds = outcomes
                    .filter(o => o.grade <= 2)
                    .map(o => o.conceptId);
                  startSession({
                    mode,
                    scope: 'weak',
                    length: weakIds.length || DEFAULT_REPLAY_LENGTH,
                    conceptIds: weakIds.length ? weakIds : undefined,
                    fresh: true,
                  });
                }}
                onPracticeAgain={() => {
                  // Repeat the same Practice-scope session with fresh quiz
                  // questions (bypass the question cache).
                  if (config) startSession({ ...config, fresh: true });
                }}
                onBackToSetup={goSetup}
              />
            </div>
          )}

          {phase === 'viewing' && viewingRecord && (
            <div className='min-h-0 flex-1 overflow-y-auto'>
              <SessionDetailView
                record={viewingRecord}
                onPracticeWeak={ids =>
                  startSession({
                    mode: viewingRecord.mode,
                    scope: 'weak',
                    length: ids.length,
                    conceptIds: ids,
                    fresh: true,
                  })
                }
                onPracticeAll={ids =>
                  startSession({
                    mode: viewingRecord.mode,
                    scope: viewingRecord.scope,
                    length: ids.length,
                    conceptIds: ids,
                    fresh: true,
                  })
                }
                onNewSession={goSetup}
              />
            </div>
          )}

          {/* Fallback when viewing was requested but the record is gone
              (effect above will clear the URL on the next tick). */}
          {phase === 'viewing' && !viewingRecord && (
            <div className='flex h-full items-center justify-center text-sm text-muted-foreground'>
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              Loading session…
            </div>
          )}
        </div>
      </div>
    </MindspaceLayout>
  );
}
